import React, { useState, useEffect, useRef, useContext } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { Spin, Button, Image, Modal } from "antd";
import {
  AudioOutlined, PlayCircleOutlined, PauseCircleOutlined, DownloadOutlined,
  PictureOutlined, FileOutlined, UserOutlined, ClockCircleOutlined,
} from "@ant-design/icons";
import { message } from "antd";
import { API_DOMAIN } from "../constants";
import { isRtlText } from "./helpers";

// ── WhatsApp text formatting ──────────────────────────────────────────────────
const URL_REGEX = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

function _fmtSegment(str) {
  const safe = str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return safe
    .replace(/\*([^*\n]+)\*/g, "<strong>$1</strong>")
    .replace(/_([^_\n]+)_/g, "<em>$1</em>")
    .replace(/~([^~\n]+)~/g, "<s>$1</s>")
    .replace(/`([^`\n]+)`/g, '<code style="background:#f5f5f5;padding:1px 4px;border-radius:3px;font-family:monospace;font-size:11px">$1</code>');
}

export function WaText({ text, onLinkClick }) {
  if (!text) return null;
  const parts = [];
  let last = 0;
  let m;
  URL_REGEX.lastIndex = 0;
  while ((m = URL_REGEX.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: "text", value: text.slice(last, m.index) });
    parts.push({ type: "link", value: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: "text", value: text.slice(last) });

  return (
    <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {parts.map((p, i) =>
        p.type === "link" ? (
          <a key={i}
            href={p.value}
            onClick={e => { e.preventDefault(); onLinkClick ? onLinkClick(p.value) : window.open(p.value.startsWith("http") ? p.value : `https://${p.value}`, "_blank", "noopener,noreferrer"); }}
            style={{ color: "#00a884", textDecoration: "underline", cursor: "pointer" }}>
            {p.value}
          </a>
        ) : (
          <span key={i} dangerouslySetInnerHTML={{ __html: _fmtSegment(p.value) }} />
        )
      )}
    </span>
  );
}

export function formatWaText(text) { return <WaText text={text} />; }

// Bold/italic/strikethrough/code only — no URL-linking, no pre-wrap. For
// compact single-line contexts (chat card previews) that need WhatsApp-style
// emphasis without the full WaText treatment, which would fight the
// container's own nowrap/ellipsis truncation.
export function formatWaTextInlineHtml(text) {
  if (!text) return "";
  return _fmtSegment(String(text));
}

// ── CSS injections ────────────────────────────────────────────────────────────
(() => {
  if (typeof document !== "undefined" && !document.getElementById("msg-status-glow-style")) {
    const s = document.createElement("style");
    s.id = "msg-status-glow-style";
    s.textContent = `
      @keyframes msgStatusGlow {
        0%   { filter: drop-shadow(0 0 0px currentColor); opacity: 1; }
        35%  { filter: drop-shadow(0 0 6px currentColor); opacity: 0.8; }
        100% { filter: drop-shadow(0 0 0px currentColor); opacity: 1; }
      }
      .cpt, .cpt * {
        transition:
          background-color 0.35s ease,
          background 0.35s ease,
          color 0.25s ease,
          border-color 0.35s ease !important;
      }
      @keyframes chatInputLaunch {
        0%   { transform: scale(1)    translateY(0);   opacity: 1; }
        40%  { transform: scale(0.96) translateY(-7px); opacity: 0.5; }
        100% { transform: scale(1)    translateY(0);   opacity: 1; }
      }
      .chat-input-launch {
        animation: chatInputLaunch 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
    `;
    document.head.appendChild(s);
  }
})();

;(() => {
  if (document.getElementById("qv4-chat-dark-style")) return;
  const s = document.createElement("style");
  s.id = "qv4-chat-dark-style";
  s.textContent = `
    .qv4-input-dark textarea::placeholder { color: rgba(233,237,239,0.45) !important; }
    .qv4-input-dark .ant-btn-text:hover { background: rgba(255,255,255,0.1) !important; }
    .qv4-input-dark .ant-btn-text:active { background: rgba(255,255,255,0.15) !important; }

    .qv4-dark-menu .ant-dropdown-menu-item,
    .qv4-dark-menu .ant-dropdown-menu-item a { color: #e9edef !important; }
    .qv4-dark-menu .ant-dropdown-menu-item:hover { background: #2a3942 !important; }
    .qv4-dark-menu .ant-dropdown-menu-item-disabled,
    .qv4-dark-menu .ant-dropdown-menu-item-disabled:hover { background: transparent !important; }
    .qv4-dark-menu .ant-dropdown-menu-item-divider { background-color: #2a3942 !important; }
  `;
  document.head.appendChild(s);
})();

// ── Message delivery status tick ──────────────────────────────────────────────
export function MsgStatus({ status }) {
  const prevRef = React.useRef(status);
  const [glowing, setGlowing] = React.useState(false);

  React.useEffect(() => {
    if (prevRef.current !== status) {
      prevRef.current = status;
      setGlowing(true);
      const t = setTimeout(() => setGlowing(false), 600);
      return () => clearTimeout(t);
    }
  }, [status]);

  if (!status) return null;

  const glowStyle = glowing
    ? { animation: "msgStatusGlow 0.6s ease-out", display: "inline-flex", alignItems: "center" }
    : { display: "inline-flex", alignItems: "center" };

  if (status === "READ") return <span style={{ ...glowStyle, color: "#4fc3f7", fontSize: 11, marginLeft: 3 }}>✓✓</span>;
  if (status === "DELIVERED") return <span style={{ ...glowStyle, color: "#8c8c8c", fontSize: 11, marginLeft: 3 }}>✓✓</span>;
  if (status === "SENT") return <span style={{ ...glowStyle, color: "#8c8c8c", fontSize: 11, marginLeft: 3 }}>✓</span>;
  if (status === "SENDING") return <ClockCircleOutlined style={{ ...glowStyle, color: "#bfbfbf", fontSize: 10, marginLeft: 3 }} />;
  if (status === "FAILED") return <span style={{ ...glowStyle, color: "#ff4d4f", fontSize: 11, marginLeft: 3 }}>✗</span>;
  return null;
}

// ── Shared contexts ───────────────────────────────────────────────────────────
// Context to pass the messages scroll container to MsgMediaContent
export const MediaScrollCtx = React.createContext(null);

// Chat panel theme — true = dark (WhatsApp-style), false = light
export const ChatThemeCtx = React.createContext(true);

// ── Theme color map ───────────────────────────────────────────────────────────
export function ct(isDark) {
  return isDark ? {
    bg: "#0b141a",
    surface: "#1f2c34",
    border: "#2a3942",
    pill: "#182229",
    bubbleIn: "#202c33",
    bubbleOut: "#005c4b",
    text: "#e9edef",
    textSub: "#8696a0",
    input: "#2a3942",
    bgFilter: "invert(1) brightness(0.6) saturate(0.35)",
    bgOpacity: 0.4,
  } : {
    bg: "#eae6df",
    surface: "#f0f2f5",
    border: "#d1d7db",
    pill: "#dfe5e7",
    bubbleIn: "#ffffff",
    bubbleOut: "#d9fdd3",
    text: "#111b21",
    textSub: "#667781",
    input: "#ffffff",
    bgFilter: "brightness(0.4) saturate(0.5)",
    bgOpacity: 0.12,
  };
}

// ── Media URL cache + resolver ────────────────────────────────────────────────
export const _mediaCache = new Map();

function _extractSignedUrlExpiry(url) {
  try {
    const u = new URL(url);
    const amzDate = u.searchParams.get("X-Amz-Date");
    const amzExpires = u.searchParams.get("X-Amz-Expires");
    if (amzDate && amzExpires) {
      const start = dayjs(amzDate, "YYYYMMDDTHHmmss[Z]");
      return start.add(parseInt(amzExpires), "second").valueOf();
    }
    const googDate = u.searchParams.get("X-Goog-Date");
    const googExpires = u.searchParams.get("X-Goog-Expires");
    if (googDate && googExpires) {
      const start = dayjs(googDate, "YYYYMMDDTHHmmss[Z]");
      return start.add(parseInt(googExpires), "second").valueOf();
    }
    const expires = u.searchParams.get("Expires");
    if (expires) return parseInt(expires) * 1000;
    const se = u.searchParams.get("se");
    if (se) return new Date(se).getTime();
  } catch { }
  return Date.now() + 3600 * 1000;
}

export function _resolveMediaUrl(rawUrl) {
  const cached = _mediaCache.get(rawUrl);
  if (cached && cached.expiresAt > Date.now() + 60_000) return Promise.resolve(cached.signedUrl);
  return axios.post(
    API_DOMAIN + "/wati_messages/parse_media_file_url",
    { file_name: rawUrl },
    { headers: { accessToken: localStorage.getItem("accessToken") } }
  ).then(res => {
    if (res.data?.success && res.data?.data) {
      const signedUrl = res.data.data;
      _mediaCache.set(rawUrl, { signedUrl, expiresAt: _extractSignedUrlExpiry(signedUrl) });
      return signedUrl;
    }
    return null;
  }).catch(() => null);
}

// ── Audio player singleton ────────────────────────────────────────────────────
let _activeAudio = null;

export function AudioPlayer({ src }) {
  const isDark = useContext(ChatThemeCtx);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const SPEEDS = [1, 1.5, 2];

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay  = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    a.addEventListener("play",  onPlay);
    a.addEventListener("pause", onPause);
    return () => {
      a.removeEventListener("play",  onPlay);
      a.removeEventListener("pause", onPause);
      if (_activeAudio === a) _activeAudio = null;
    };
  }, []); // eslint-disable-line

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      if (_activeAudio === a) _activeAudio = null;
    } else {
      if (_activeAudio && _activeAudio !== a) _activeAudio.pause();
      _activeAudio = a;
      a.play().catch(() => {});
    }
  };

  const cycleSpeed = () => {
    const a = audioRef.current;
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    setSpeed(next);
    if (a) a.playbackRate = next;
  };

  const handleSeek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = pct * duration;
    setCurrentTime(pct * duration);
  };

  const fmt = s => {
    if (!s || isNaN(s)) return "0:00";
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
  };

  const pct = duration ? (currentTime / duration) * 100 : 0;
  const iconClr = isDark ? "#e9edef" : "#111b21";
  const trackBg = isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)";
  const subClr = isDark ? "#8696a0" : "#667781";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, width: 210 }}>
      <audio ref={audioRef} src={src}
        onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
        onLoadedMetadata={e => setDuration(e.target.duration)}
        onEnded={() => { setCurrentTime(0); if (_activeAudio === audioRef.current) _activeAudio = null; }}
      />
      <Button type="text" size="small" icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
        onClick={togglePlay}
        style={{ color: iconClr, flexShrink: 0, padding: 0, fontSize: 20, width: 28, height: 28 }}
      />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
        <div onClick={handleSeek}
          style={{ height: 3, background: trackBg, borderRadius: 2, cursor: "pointer", position: "relative" }}>
          <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${pct}%`, background: "#25d366", borderRadius: 2 }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontSize: 9, color: subClr }}>{fmt(currentTime)}</span>
          <span style={{ fontSize: 9, color: subClr }}>{fmt(duration)}</span>
        </div>
      </div>
      <Button type="text" size="small" onClick={cycleSpeed}
        style={{ color: subClr, fontSize: 10, fontWeight: 700, flexShrink: 0, padding: "0 3px", minWidth: 26, height: 22 }}>
        {speed}x
      </Button>
    </div>
  );
}

// ── Contacts bubble ───────────────────────────────────────────────────────────
export function ContactsBubble({ contacts }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 200, maxWidth: 280 }}>
      {(contacts || []).map((c, i) => {
        const name = c.name?.formatted_name || "Contact";
        const phones = c.phones || [];
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "rgba(0,0,0,0.07)", borderRadius: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#9e9e9e", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <UserOutlined style={{ fontSize: 20, color: "#fff" }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, wordBreak: "break-word", overflowWrap: "anywhere" }}>{name}</div>
              {phones.map((p, j) => (
                <div key={j} style={{ fontSize: 11, color: "#667781", marginTop: 1 }}>{p.phone}</div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Media content renderer ────────────────────────────────────────────────────
export function MsgMediaContent({ item }) {
  const type = (item.type || "").toLowerCase();
  const rawUrl = item.data || null;
  const localUrl = item._localPreviewUrl || null;
  const scrollCtx = useContext(MediaScrollCtx);

  const cachedEntry = rawUrl && !localUrl ? _mediaCache.get(rawUrl) : null;
  const initialUrl = localUrl || (cachedEntry && cachedEntry.expiresAt > Date.now() + 60_000 ? cachedEntry.signedUrl : null);

  const [signedUrl, setSignedUrl] = useState(initialUrl);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [imgCachedUrl, setImgCachedUrl] = useState(initialUrl || null);
  const containerRef = useRef(null);

  const handleDownload = (e) => {
    e.stopPropagation();
    if (!rawUrl || downloading) return;
    setDownloading(true);
    axios.post(
      API_DOMAIN + "/wati_messages/download_wati_media_file",
      { file_name: rawUrl },
      { headers: { accessToken: localStorage.getItem("accessToken") }, responseType: "blob" }
    ).then(res => {
      const contentDisposition = res.headers["content-disposition"] || "";
      const nameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/i);
      const fileName = nameMatch?.[1] || item.fileName || rawUrl.split("/").pop() || "download";
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = fileName;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(blobUrl);
    }).catch(() => message.error("Download failed."))
      .finally(() => setDownloading(false));
  };

  useEffect(() => {
    if (!signedUrl || imgCachedUrl === signedUrl) return;
    if ((item.type || "").toLowerCase() !== "image") {
      setImgCachedUrl(signedUrl);
      return;
    }
    const img = new window.Image();
    img.onload = () => {
      const scrollEl = scrollCtx?.current;
      const prevH = scrollEl?.scrollHeight;
      setImgCachedUrl(signedUrl);
      if (scrollEl && prevH != null) {
        requestAnimationFrame(() => {
          const delta = scrollEl.scrollHeight - prevH;
          if (delta <= 0) return;
          const currTop = scrollEl.scrollTop;
          const isNearBottom = scrollEl.scrollHeight - currTop - scrollEl.clientHeight < 80;
          if (isNearBottom) {
            scrollEl.scrollTop = scrollEl.scrollHeight;
          } else {
            const cRect = containerRef.current?.getBoundingClientRect();
            const sRect = scrollEl.getBoundingClientRect();
            if (cRect && cRect.top < sRect.top + scrollEl.clientHeight) {
              scrollEl.scrollTop = currTop + delta;
            }
          }
        });
      }
    };
    img.onerror = () => setImgCachedUrl(signedUrl);
    img.src = signedUrl;
  }, [signedUrl]); // eslint-disable-line

  useEffect(() => {
    if (!rawUrl || signedUrl || localUrl) return;
    const el = containerRef.current;
    const root = scrollCtx?.current || null;
    if (!el) return;
    let settled = false;

    const load = () => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      setLoading(true);
      _resolveMediaUrl(rawUrl).then(url => {
        const scrollEl = scrollCtx?.current;
        const prevH = scrollEl?.scrollHeight;
        setSignedUrl(url);
        setLoading(false);
        if (scrollEl) {
          requestAnimationFrame(() => requestAnimationFrame(() => {
            const delta = scrollEl.scrollHeight - prevH;
            if (delta <= 0) return;
            const currTop = scrollEl.scrollTop;
            const isNearBottom = scrollEl.scrollHeight - currTop - scrollEl.clientHeight < 80;
            if (isNearBottom) {
              scrollEl.scrollTop = scrollEl.scrollHeight;
            } else {
              const cRect = containerRef.current?.getBoundingClientRect();
              const sRect = scrollEl.getBoundingClientRect();
              if (cRect && cRect.top < sRect.top + scrollEl.clientHeight) {
                scrollEl.scrollTop = currTop + delta;
              }
            }
          }));
        }
      });
    };

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) load();
    }, { root, threshold: 0, rootMargin: "120px 0px" });
    observer.observe(el);

    // Guards against a race with a programmatic auto-scroll landing in the
    // same frame (e.g. scroll-to-bottom after switching chats in a pinned
    // drawer): the observer can attach while the pane is still at its old
    // scroll position, see this item as off-screen, and then never get a
    // fresh check since the scroll happened via scrollIntoView rather than a
    // user gesture. Re-measure directly once that scroll has had a chance to
    // settle, as a fallback trigger.
    const recheckId = requestAnimationFrame(() => requestAnimationFrame(() => {
      if (settled) return;
      const elRect = el.getBoundingClientRect();
      const rootRect = root
        ? root.getBoundingClientRect()
        : { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
      const visible = elRect.bottom >= rootRect.top - 120 && elRect.top <= rootRect.bottom + 120 &&
        elRect.right >= rootRect.left && elRect.left <= rootRect.right;
      if (visible) load();
    }));

    return () => { observer.disconnect(); cancelAnimationFrame(recheckId); };
  }, [rawUrl, signedUrl]); // eslint-disable-line

  const spinner = <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 48 }}><Spin size="small" /></div>;

  const dlBtn = signedUrl && (
    <span onClick={handleDownload}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: "50%", background: "rgba(0,0,0,0.45)", color: "#fff", flexShrink: 0, cursor: downloading ? "not-allowed" : "pointer" }}>
      {downloading ? <Spin size="small" style={{ fontSize: 11 }} /> : <DownloadOutlined style={{ fontSize: 13 }} />}
    </span>
  );

  const caption = item.caption || item.text || null;
  const captionEl = caption ? <div style={{ fontSize: 12, marginTop: 4, wordBreak: "break-word", overflowWrap: "break-word", width: 0, minWidth: "100%", boxSizing: "border-box", ...(isRtlText(caption) ? { direction: "rtl", textAlign: "right" } : {}) }}>{formatWaText(caption)}</div> : null;

  if (type === "image") return (
    <div ref={containerRef} style={{ display: "inline-flex", flexDirection: "column", maxWidth: "100%" }}>
      {loading ? <div style={{ width: 260, height: 180, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin /></div>
        : imgCachedUrl
          ? <div style={{ position: "relative", alignSelf: "flex-start", cursor: "pointer" }}>
              <div style={{ width: 260, height: 200, borderRadius: 8, overflow: "hidden" }}>
                <Image src={imgCachedUrl} alt="Photo"
                  wrapperStyle={{ width: "100%", height: "100%", display: "block" }}
                  style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block", borderRadius: 8 }}
                  preview={{ mask: false }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==" />
              </div>
              <div style={{ position: "absolute", top: 6, right: 6 }}>{dlBtn}</div>
            </div>
          : <div style={{ width: 260, height: 200, background: "rgba(0,0,0,0.06)", borderRadius: 8, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#8c8c8c", cursor: "pointer" }}><PictureOutlined style={{ fontSize: 28 }} /><div style={{ fontSize: 11, marginTop: 4 }}>Photo</div></div>
      }
      {captionEl}
    </div>
  );
  if (type === "video") return (
    <div ref={containerRef} style={{ display: "inline-flex", flexDirection: "column", maxWidth: "100%" }}>
      {loading ? <div style={{ width: 260, height: 180, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin /></div> : signedUrl
        ? <>
          <div onClick={() => setVideoOpen(true)}
            style={{ position: "relative", display: "inline-block", maxWidth: "100%", cursor: "pointer", borderRadius: 8, overflow: "hidden" }}>
            <video src={signedUrl} preload="metadata"
              style={{ display: "block", width: 260, height: "auto", maxHeight: 320, borderRadius: 8, pointerEvents: "none" }} />
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.3)" }}>
              <PlayCircleOutlined style={{ fontSize: 40, color: "#fff" }} />
            </div>
            <div style={{ position: "absolute", top: 6, right: 6 }} onClick={e => e.stopPropagation()}>{dlBtn}</div>
          </div>
          <Modal open={videoOpen} onCancel={() => setVideoOpen(false)} footer={null} centered
            width={Math.min(window.innerWidth * 0.88, 900)}
            styles={{ body: { padding: 0, background: "#000", borderRadius: 8, overflow: "hidden" }, mask: { backdropFilter: "blur(4px)" } }}
            closable={false} destroyOnHidden>
            {videoOpen && (
              <video src={signedUrl} controls autoPlay
                style={{ width: "100%", maxHeight: "75vh", display: "block", outline: "none" }} />
            )}
          </Modal>
        </>
        : <div style={{ width: 180, height: 180, background: "rgba(0,0,0,0.75)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <PlayCircleOutlined style={{ fontSize: 36, color: "#fff", opacity: 0.85 }} />
        </div>
      }
      {captionEl}
    </div>
  );
  if (type === "audio") return (
    <div ref={containerRef}>
      {loading ? spinner : signedUrl
        ? <AudioPlayer src={signedUrl} />
        : <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 2px" }}>
          <AudioOutlined style={{ fontSize: 18, color: "#8c8c8c" }} />
          <span style={{ fontSize: 12, color: "#595959" }}>Voice message</span>
        </div>
      }
      {captionEl}
    </div>
  );
  if (type === "document") return (
    <div ref={containerRef}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 2px", height: 44, cursor: "pointer" }}>
        <FileOutlined style={{ fontSize: 20, color: "#8c8c8c", flexShrink: 0 }} />
        <span style={{ fontSize: 12, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.fileName || "Document"}</span>
        {loading ? <Spin size="small" /> : dlBtn}
      </div>
      {captionEl}
    </div>
  );
  if (type === "sticker") return (
    <div ref={containerRef} style={{ display: "inline-block" }}>
      {loading
        ? <div style={{ width: 100, height: 100, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin /></div>
        : signedUrl
          ? <Image src={signedUrl} alt="Sticker"
            style={{ width: 100, height: 100, objectFit: "contain", display: "block" }}
            preview={{ mask: false }} />
          : <div style={{ width: 100, height: 100, display: "flex", alignItems: "center", justifyContent: "center", color: "#8c8c8c", fontSize: 11 }}>Sticker</div>
      }
    </div>
  );
  return null;
}

// ── Broadcast header media ────────────────────────────────────────────────────
export function BroadcastHeaderMedia({ rawPath, mediaType }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const scrollCtx = useContext(MediaScrollCtx);

  useEffect(() => {
    if (!rawPath || url) return;
    const el = ref.current;
    const root = scrollCtx?.current || null;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      setLoading(true);
      _resolveMediaUrl(rawPath).then(u => { setUrl(u); setLoading(false); });
    }, { root, threshold: 0, rootMargin: "120px 0px" });
    observer.observe(el);
    return () => observer.disconnect();
  }, [rawPath]); // eslint-disable-line

  if (!rawPath) return null;
  const mt = (mediaType || "image").toLowerCase();
  return (
    <div ref={ref} style={{ marginBottom: 4 }}>
      {loading && <div style={{ width: 260, height: 180, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin size="small" /></div>}
      {!loading && url && mt === "image" && (
        <Image src={url} alt="" style={{ width: 260, height: "auto", maxHeight: 320, display: "block", borderRadius: 6 }} preview={{ mask: false }} />
      )}
      {!loading && url && mt === "video" && (
        <video src={url} controls style={{ width: 260, height: "auto", maxHeight: 320, borderRadius: 6, display: "block" }} />
      )}
      {!loading && url && mt === "audio" && (
        <audio src={url} controls style={{ maxWidth: 280, marginBottom: 4 }} />
      )}
    </div>
  );
}

// ── Quote reply media thumbnail ───────────────────────────────────────────────
export function QuoteMediaThumb({ type, rawPath }) {
  const [url, setUrl] = useState(null);
  const [duration, setDuration] = useState(null);

  useEffect(() => {
    if (!rawPath) return;
    _resolveMediaUrl(rawPath).then(u => { if (u) setUrl(u); });
  }, [rawPath]); // eslint-disable-line

  const fmtDur = secs => {
    if (!secs || !isFinite(secs)) return "Voice";
    return `${Math.floor(secs / 60)}:${String(Math.floor(secs % 60)).padStart(2, "0")}`;
  };

  const t = (type || "").toLowerCase();

  if (t === "audio") return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      {url && <audio src={url} preload="metadata" style={{ display: "none" }} onLoadedMetadata={e => setDuration(e.target.duration)} />}
      <AudioOutlined style={{ fontSize: 12 }} />
      {fmtDur(duration)}
    </span>
  );

  const S = 44;
  const box = { width: S, height: S, borderRadius: 6, flexShrink: 0, overflow: "hidden", display: "block" };
  if (!url) return <div style={{ ...box, background: "rgba(0,0,0,0.08)" }} />;

  if (t === "image") return <img src={url} alt="" style={{ ...box, objectFit: "cover" }} />;

  if (t === "video") return (
    <div style={{ ...box, position: "relative" }}>
      <video src={url} muted preload="metadata"
        style={{ width: S, height: S, objectFit: "cover", display: "block", pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.28)" }}>
        <PlayCircleOutlined style={{ color: "#fff", fontSize: 15 }} />
      </div>
    </div>
  );

  return null;
}
